import {DateTimePicker, Picker, Switch, t} from "owc-react-lib-common";
import React from 'react';
import {Loader} from 'src/components/Loader';
import {ITableHeader, Table} from 'src/components/Table';
import {Text} from "src/components/Text";
import {Title} from 'src/components/Title';
import {View} from "src/components/View";
import {useAlerts} from "src/hooks/useAlerts";
import {useComponentDidMount} from 'src/hooks/useComponentDidMount';
import * as EventService from 'src/services/EventService';
import {IEventCategoryDTO, IEventDTO} from 'src/services/EventService';
import * as MatchService from "src/services/MatchService";
import {IMatchDTO} from "src/services/MatchService";
import * as GroupService from "src/services/GroupService";
import {IGroupDTO} from "src/services/GroupService";
import * as StringUtils from 'src/utils/StringUtils';
import {EventContext} from '../EventContext';
import {Button} from "../../../../components/Button";
import moment from "moment";
import {ModalConfirm} from "../../../../components/ModalConfirm";
import {StyleSheet} from "react-native";
import {Modal} from "../../../../components/Modal";


interface Filter {
  categoryId?: number,
  groupId?: number,
  startDate?: Date,
  endDate?: Date,
}

interface ActivateAllResponseDTO {
  errors:
    {
      matchId: number,
      error: string
    }[]
}
export const AIBasketMatchesList = () => {
  const alerts = useAlerts();

  const {data: event} = React.useContext(EventContext) as { data: IEventDTO };
  const [matches, setMatches] = React.useState<IMatchDTO[]>();
  const [eventCategories, setEventCategories] = React.useState<IEventCategoryDTO[]>();
  const [groups, setGroups] = React.useState<IGroupDTO[]>();

  const [loading, setLoading] = React.useState(true);
  const [loadingMatches, setLoadingMatches] = React.useState(new Set());
  const [activatingAll, setActivatingAll] = React.useState<boolean>(false);
  const [activateAllModalOpen, setActivateAllModalOpen] = React.useState<boolean>(false);
  const [activateAllErrors, setActivateAllErrors] = React.useState<ActivateAllResponseDTO>();
  const [errorModalOpen, setErrorModalOpen] = React.useState<boolean>(false);

  const [filter, setFilter] = React.useState<Filter>({
    startDate: getStartOfDayDate(event.beginDate),
    endDate: getEndOfDayDate(event.endDate),
  });


  function getStartOfDayDate(date: string) {
    return date ? moment(date).startOf("day").toDate() : moment().startOf("day").toDate();
  }

  function getEndOfDayDate(date: string) {
    return date ? moment(date).endOf("day").toDate() : moment().endOf("day").toDate();
  }

  /**
   * component did mount
   */
  useComponentDidMount(async () => {
    try {
      const [matchResponse, eventCategoryResponse, groupsResponse] = await Promise.all([
        MatchService.getAIBasketActivableMatchesByEventId(event.id),
        EventService.findEventCategoriesforEvent(event.uuid),
        GroupService.getByEventId(event.id)
      ]);

      setMatches(matchResponse.data);
      setEventCategories(eventCategoryResponse.data);
      setGroups(groupsResponse.data);

    } catch (error) {
      alerts.createDSMError(error);
    }

    setLoading(false);
  });

  const filteredMatches = React.useMemo(() => {
    return matches?.filter(match =>
      (filter.categoryId == null || match.eventCategoryId === filter.categoryId) &&
      (filter.groupId == null || match.groupId === filter.groupId) &&
      (filter.startDate == null || new Date(match.matchDateHour) >= filter.startDate) &&
      (filter.endDate == null || new Date(match.matchDateHour) <= filter.endDate)
    );
  }, [matches, filter]);


  const handleDateChange = (val: { startDate: string; endDate: string; }) => {
    const startDate = getStartOfDayDate(val.startDate);
    const selectedEndDate = getEndOfDayDate(val.endDate);

    const endDate =
      startDate >= selectedEndDate || selectedEndDate <= filter.startDate
        ? getEndOfDayDate(val.startDate)
        : selectedEndDate;

    setFilter({...filter, startDate: startDate, endDate: endDate});
  };

  const handleMatchStateChange = async (match: IMatchDTO, newState: boolean) => {
    setLoadingMatches((prev) => new Set(prev).add(match.id));

    try {
      const matchResponse = newState
        ? await MatchService.activateAIBasket(match.id)
        : await MatchService.deactivateAIBasket(match.id);

      setMatches(prevState =>
        prevState.map(m =>
          m.id === match.id
            ? {...m, recordingAllowed: matchResponse.data.recordingAllowed}
            : m
        )
      );

      alerts.create({type: 'success', text: t('global.success')});
    } catch (error) {
      alerts.createDSMError(error);
    }

    setLoadingMatches((prev) => {
      const newLoadingSet = new Set(prev);
      newLoadingSet.delete(match.id);
      return newLoadingSet;
    });
  };

  const handleActivateAll = async () => {
    const matchesToActivate = filteredMatches.filter(m => !m.recordingAllowed);

    setLoadingMatches((prev) => {
      const newLoadingSet = new Set(prev);
      matchesToActivate
        .map(m => m.id)
        .forEach(newLoadingSet.add, newLoadingSet);
      return newLoadingSet;
    });

    setActivatingAll(true);
    setActivateAllModalOpen(false);
    
    try {
      const activateAllResponse = await MatchService.activateAIBasketAll({
        matchIds: matchesToActivate.map(m => m.id)
      });

      setMatches((await MatchService.getAIBasketActivableMatchesByEventId(event.id)).data);

      if (activateAllResponse.data.errors?.length) {
        setErrorModalOpen(true);
        setActivateAllErrors(activateAllResponse.data);
      }

      if (activateAllResponse.data.errors?.length < matchesToActivate.length) {
        alerts.create({type: 'success', text: t('global.success')});
      } else {
        alerts.create({type: 'error', text: t('errors.default')});
      }
    } catch (error) {
      alerts.createDSMError(error);
    }

    setActivatingAll(false);
    setLoadingMatches((prev) => {
      const newLoadingSet = new Set(prev);
      matchesToActivate
        .map(m => m.id)
        .forEach(newLoadingSet.delete, newLoadingSet);
      return newLoadingSet;
    });
  };

  /**
   * loading
   */
  if (loading) {
    return <Loader/>;
  }

  /**
   * render
   */
  const headers: ITableHeader[] = [
    {
      name: "matchCallId",
      label: t("admin.events.matches.id"),
      width: 40,
    },
    {
      name: "eventCategoryName",
      label: t("admin.events.matches.category"),
      textProps: { uppercase: true },
    },
    {
      name: "groupName",
      label: t("admin.events.matches.group"),
      textProps: { uppercase: true },
    },
    {
      name: "facilityName",
      label: t("admin.events.matches.facility"),
      textProps: { uppercase: true },
    },
    {
      name: "courtName",
      label: t("admin.events.matches.court"),
      textProps: { uppercase: true },
    },
    {
      name: "formattedMatchDateHour",
      label: t("admin.events.matches.date"),
      sort: (match) => match.matchDateHour,
    },
    {
      name: "homeTeamName",
      label: t("admin.events.matches.homeTeam"),
      headerTextProps: { textAlign: "left" },
      render: (match: any) => {
        if (match.homeTeamRef) {
          return <Text textAlign="left">[{StringUtils.minifyTeamName(match.homeTeamRef)?.toUpperCase()}] {match.homeTeamName}</Text>;
        }

        return <Text textAlign="left">{match.homeTeamName}</Text>;
      },
    },
    {
      name: "awayTeamName",
      label: t("admin.events.matches.awayTeam"),
      textProps: { uppercase: true },
      render: (match: any) => {
        if (match.awayTeamRef) {
          return <Text>[{StringUtils.minifyTeamName(match.awayTeamRef)?.toUpperCase()}] {match.awayTeamName}</Text>;
        }

        return <Text>{match.awayTeamName}</Text>;
      },
    },
    {
      name: "actions",
      label: "AIBasket",
      render: (match: any) =>
        <View flexDirection={'row'}>
          {loadingMatches.has(match.id) && <Loader size={'small'} margin={0}/>}
          <Switch
            value={match.recordingAllowed}
            onChange={(newState: boolean) => handleMatchStateChange(match, newState)}
            containerStyle={{marginLeft: 'auto', marginRight: 10}}
          />
        </View>,
      width: 100,
    },
  ];

  function eventCategoryItems() {
    return eventCategories.map(eventCategory => ({
      label: eventCategory.name.toUpperCase(),
      value: eventCategory.id
    }));
  }

  function groupItems() {
    return groups?.filter(group => group.eventCategoryId === filter.categoryId).map(g => ({
      label: g.name.toUpperCase(),
      value: g.id
    }));
  }

  return (
    <View>
      <View flexDirection='row' alignItems='center' marginBottom={30}>
        <Title text={t('admin.events.matches.aibasket.aibasketMatches')} containerStyle={{ flex: 1, marginBottom: 0 }} />

      </View>
        <View flexDirection={'row'}>
          <Picker label={t('admin.events.matches.category')}
                  selected={filter.categoryId || ''}
                  containerStyle={styles.filterInput}
                  items={eventCategoryItems()}
                  defaultOptionLabel={t('global.all')}
                  onChange={(val) => setFilter({...filter, categoryId: parseInt(val) || undefined, groupId: undefined})}
          />
          <Picker label={t('admin.events.matches.group')}
                  selected={filter.groupId || ''}
                  containerStyle={styles.filterInput}
                  items={groupItems()}
                  defaultOptionLabel={t('global.all')}
                  disabled={!filter.categoryId}
                  onChange={(val) => setFilter({...filter, groupId: parseInt(val) || undefined})}
          />
          <DateTimePicker showLabels
                          range
                          gap={20}
                          flexDirection={'row'}
                          format={'YYYY-MM-DD'}
                          showTime={false}
                          containerStyle={styles.filterInput}
                          startDateValue={filter.startDate}
                          endDateValue={filter.endDate}
                          onChange={handleDateChange}
          />
          <View style={styles.activateAllBtnContainer}>
            <Button label={t('admin.events.matches.aibasket.activateAll')}
                    containerStyle={styles.activateAllBtn}
                    type={'primary'}
                    loading={activatingAll}
                    disabled={filteredMatches.every(m => m.recordingAllowed)}
                    onPress={() => setActivateAllModalOpen(true)}
            />
          </View>
        </View>
      <Table headers={headers} data={filteredMatches} />

      <ModalConfirm visible={activateAllModalOpen}
                    text={t('admin.events.matches.aibasket.activateAllDescription')}
                    closeLabel={t('global.close')}
                    onClose={() => setActivateAllModalOpen(false)}
                    onSuccess={handleActivateAll}
      />

      <Modal visible={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
        <Text style={{...styles.text, marginBottom: 15}}>{t('admin.events.matches.aibasket.activateAllIssues') + ':'}</Text>
        {
          activateAllErrors?.errors
            .sort((a, b) => a.matchId - b.matchId)
            .map((m) =>
              <View key={`activate-all-error-${m.matchId}`} style={{flexDirection: 'row', marginBottom: 5}}>
                <Text style={{...styles.text, textAlign: 'left', flex: 1}}>{'#' + m.matchId}</Text>
                <Text style={{...styles.text, textAlign: 'left', flex: 5}}>{m.error}</Text>
              </View>
            )
        }
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  filterInput: {
    marginTop: 6,
    marginRight: 20,
  },
  activateAllBtnContainer: {
    height: '50%',
    marginLeft: 'auto',
    marginTop: 'auto',
    marginBottom: 'auto'
  },
  activateAllBtn: {
    marginLeft: 'auto'
  },
  text: {
    fontSize: 14,
  },
});